import React, { useEffect, useState } from "react";
import Button from "../../../../components/ui/Button";
import Card from "../../../../components/ui/card";
import { Edit, MapPin, Search, X } from "lucide-react";
import Autocomplete from "react-google-autocomplete";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../redux";
import { getAllCountries } from "../../../../redux/Actions/AdminAction";
import { extractLocationFromPlace } from "../../../../lib/locationUtils";
import { useTranslation } from "react-i18next";

export default function LocationComponent({
    editingLocation,
    isEditing,
    startEditingLocation,
    editedProfile,
    handleFieldChange,
    validationPersonalErrors,
    profileData,
    handleLocationSaveChanges,
    setEditingLocation,
    isCurrentUser = true,
    showEditIcon = true,
    canEdit = true,
}) {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useAppDispatch();
    const [allCountries, setAllCountries] = useState<any[]>([]);

    // Google Maps API key - you'll need to replace this with your actual API key
    const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "YOUR_GOOGLE_MAPS_API_KEY";

    // Fetch all countries once on mount (paginate to match totalCount)
    useEffect(() => {
        const fetchAllCountries = async () => {
            try {
                const limit = 100;
                let page = 1;
                let combined: any[] = [];

                const first = await dispatch(getAllCountries({ page, limit, isShowLoader: false })).unwrap();
                const firstData = Array.isArray(first?.data) ? first.data : Array.isArray(first) ? first : [];
                const totalCount = typeof first?.totalCount === "number" ? first.totalCount : firstData.length;
                combined = combined.concat(firstData);

                const totalPages = Math.max(1, Math.ceil(totalCount / limit));
                for (page = 2; page <= totalPages; page++) {
                    const res = await dispatch(getAllCountries({ page, limit, isShowLoader: false })).unwrap();
                    const pageData = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
                    combined = combined.concat(pageData);
                }

                // Ensure we only keep up to totalCount entries
                setAllCountries(combined.slice(0, totalCount));
            } catch (err) {
                // Fallback: keep whatever was fetched
                console.error("Failed to fetch all countries:", err);
            }
        };
        fetchAllCountries();
    }, [dispatch]);

    // Initialize search query when editing starts
    useEffect(() => {
        if (editingLocation || isEditing && editedProfile) {
            setSearchQuery(editedProfile?.address || editedProfile?.location || "");
        }
    }, [editingLocation, isEditing, editedProfile?.address, editedProfile?.location]);

    // Handle place selection from Google Autocomplete
    const handlePlaceSelect = (place: any) => {
        if (place) {
            setIsLoading(true);
            
            try {
                console.log("Raw place object:", place);
                
                // Use utility to extract country, province, coords, etc.
                const locationData = extractLocationFromPlace(place);

                // Update address-related fields
                const address = place.formatted_address || "";
                handleFieldChange("address", address);
                handleFieldChange("latitude", locationData.latitude);
                handleFieldChange("longitude", locationData.longitude);
                handleFieldChange("location", address); // For backward compatibility

                // Province/state name
                if (locationData.province) {
                    handleFieldChange("province", locationData.province);
                }

                // Map country name to countryId from fetched list
                if (locationData.country && Array.isArray(allCountries) && allCountries.length > 0) {
                    const match = allCountries.find((c: any) => {
                        const name = (c.name || "").toLowerCase();
                        const initial = (c.initial || c.code || "").toLowerCase();
                        const target = locationData.country.toLowerCase();
                        return name === target || initial === target;
                    });
                    if (match?.id) {
                        handleFieldChange("country", { id: match.id });
                    }
                }

                setSearchQuery(address);
                console.log("Fields updated successfully (with country/province)");
            } catch (error) {
                console.error("Error processing selected place:", error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    // Clear all location fields
    const clearLocation = () => {
        setSearchQuery("");
        handleFieldChange("address", "");
        handleFieldChange("latitude", "");
        handleFieldChange("longitude", "");
        handleFieldChange("location", "");
        handleFieldChange("province", "");
        handleFieldChange("country", { id: "" });
    };

    return (
        <>
            <Card color={undefined} className="p-4 w-full">
                <h2 className="text-lg font-semibold mb-4 flex items-center justify-between">
                    {t("locationComponent.title")}
                    {!editingLocation && !isEditing && isCurrentUser && showEditIcon && canEdit && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={startEditingLocation}
                            className="text-blue-600 hover:text-blue-700">
                            <Edit className="h-4 w-4" />
                        </Button>
                    )}
                </h2>
                {editingLocation || isEditing ? (
                    <div className="space-y-4">
                        {/* Google Maps Autocomplete Search */}
                        <div className="relative">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("locationComponent.cityRegionLabel")}
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                                    <Search className="h-4 w-4 text-gray-400" />
                                </div>
                                <Autocomplete
                                    apiKey={GOOGLE_MAPS_API_KEY}
                                    onPlaceSelected={handlePlaceSelect}
                                    options={{
                                        types: ["geocode"],
                                        componentRestrictions: { country: [] }
                                    }}
                                    value={searchQuery}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                                    placeholder={t("locationComponent.searchPlaceholder")}
                                    className="w-full pl-10 pr-10 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                                    disabled={isLoading}
                                />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        onClick={clearLocation}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 z-10"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                            
                            {/* Loading indicator */}
                            {isLoading && (
                                <div className="absolute right-3 top-8">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                </div>
                            )}
                        </div>

                        {/* Display selected location details */}
                        {(editedProfile?.address || editedProfile?.latitude || editedProfile?.longitude) && (
                            <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                                <h4 className="text-sm font-medium text-gray-700">{t("locationComponent.selectedLocationDetails")}</h4>
                                {editedProfile?.address && (
                                    <div className="text-sm">
                                        <span className="font-medium">{t("locationComponent.address")}</span> {editedProfile.address}
                                    </div>
                                )}
                                {(editedProfile?.latitude || editedProfile?.longitude) && (
                                    <div className="text-sm">
                                        <span className="font-medium">{t("locationComponent.coordinates")}</span>{" "}
                                        {editedProfile?.latitude && editedProfile?.longitude
                                            ? `${editedProfile.latitude.toFixed(6)}, ${editedProfile.longitude.toFixed(6)}`
                                            : t("locationComponent.incompleteCoordinates")}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Validation error display */}
                        {validationPersonalErrors?.personal?.location && (
                            <p className="text-red-500 text-xs mt-1">
                                {validationPersonalErrors.personal.location}
                            </p>
                        )}

                        <div className="flex justify-end gap-2">
                            <Button variant="outline" className="text-sm" onClick={() => setEditingLocation(false)}>
                                {t("locationComponent.cancel")}
                            </Button>
                            <Button className="text-sm" onClick={handleLocationSaveChanges}>
                                {t("locationComponent.save")}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3 text-sm">
                        <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            {(profileData.personal?.address || profileData.personal?.location) ? (
                                <div className="space-y-1">
                                    <span className="text-blue-600 hover:underline">
                                        {profileData.personal.address || profileData.personal.location}
                                    </span>
                                    {(profileData.personal.latitude || profileData.personal.longitude) && (
                                        <div className="text-xs text-gray-500">
                                            {t("locationComponent.coordinates")} {profileData.personal.latitude?.toFixed(6)}, {profileData.personal.longitude?.toFixed(6)}
                                        </div>
                                    )}
                                    {profileData.personal.province && (
                                        <div className="text-xs text-gray-500">
                                            {t("locationComponent.province")} {profileData.personal.province}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <span className="text-gray-500">{t("locationComponent.noLocationProvided")}</span>
                            )}
                        </div>
                    </div>
                )}
            </Card>
        </>
    );
}
